const PictViewClass = require('pict-view');

// Available signature font styles. Form authors pick one via
// `MyDefaultFormManifest.SignatureSectionFontStyle`. Only the font for the
// selected style is fetched at render time.
const SIGNATURE_FONT_STYLES = {
	Default: { FontFamily: 'Rock Salt', URLParam: 'Rock+Salt', CSSClass: 'hl-signature-font-default' },
	Cursive: { FontFamily: 'Corinthia', URLParam: 'Corinthia', CSSClass: 'hl-signature-font-cursive' },
	Serif: { FontFamily: 'Merriweather', URLParam: 'Merriweather', CSSClass: 'hl-signature-font-serif' },
	'Sans-serif': { FontFamily: 'Cabin', URLParam: 'Cabin', CSSClass: 'hl-signature-font-sans-serif' },
};

// Approval actions that sign a document as its submitter: the first signature row.
const SUBMISSION_ACTIONS = [ 'submit', 'complete' ];
// Statuses no further signature follows, so the "more signatures may appear" note is hidden.
const FINAL_STATUSES = [ 'approved', 'completed' ];

class HeadlightSignatures extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		// TODO: Port this over to Pict, don't leave it here
		// Example: {~DateFormatTZ:AppData.Date^MM/YYYY^AppData.DocumentData.Timezone^~}
		this.pict.MetaTemplate.addPattern('{~DateFormatTZ:', '~}', (pHash, pData, fCallback) =>
		{
			let tmpHash = pHash.trim();
			let tmpData = (typeof (pData) === 'object') ? pData : {};
			let tmpDateValueSet = tmpHash.split('^');

			let date = pData.UpdateDate; // NOTE: using instead of 'tmpDataValueSet[0]' for now (needs a "Record" look up)
			let format = tmpDateValueSet[1] || '';
			let timezone = tmpDateValueSet[2] ? this.pict.resolveStateFromAddress(tmpDateValueSet[2]) : null

			let tmpDayJS = pFable.Dates.dayJS.utc(date);
			try
			{
				if (timezone)
				{
					tmpDayJS = tmpDayJS.tz(timezone);
				}
				else
				{
					try
					{
						tmpDayJS = tmpDayJS.tz(pFable.Dates.dayJS.tz.guess());
					}
					catch (pError)
					{
						this.log.error(`Error guessing dayJS guess() function; dates may be formatted to GMT by default. (${pError.message || pError})`);
					}
				}
			}
			catch
			{
				try
				{
					tmpDayJS = tmpDayJS.tz(pFable.Dates.dayJS.tz.guess());
				}
				catch (pError)
				{
					this.log.error(`Error guessing dayJS guess() function; dates may be formatted to GMT by default. (${pError.message || pError})`);
				}
			}
			return tmpDayJS.format(format);
		});

		this.SignatureData = {
			SignaturesList: [],
			IsApproved: false,
			IsFinal: false,
			Font: SIGNATURE_FONT_STYLES.Default,
			Options: {
				HideSectionHeader: false,
				ShowTitles: true,
				HideDates: false,
				CustomLabels: []
			}
		};
	}

	/**
	 * Resolve the configured signature font style from the manifest.
	 * Form authors set this via `MyDefaultFormManifest.SignatureSectionFontStyle`
	 * before the form is published — it is read once per render and not expected
	 * to change while a form is in active use.
	 *
	 * @returns {{ FontFamily: string, URLParam: string, CSSClass: string }}
	 */
	resolveSignatureFontStyle()
	{
		const tmpConfiguredStyle = this.pict?.settings?.MyDefaultFormManifest?.SignatureSectionFontStyle;
		if (!tmpConfiguredStyle)
		{
			return SIGNATURE_FONT_STYLES.Default;
		}
		const tmpNormalized = String(tmpConfiguredStyle).toLowerCase();
		const tmpMatchedKey = Object.keys(SIGNATURE_FONT_STYLES).find((pKey) => pKey.toLowerCase() === tmpNormalized);
		if (tmpMatchedKey)
		{
			return SIGNATURE_FONT_STYLES[tmpMatchedKey];
		}
		this.pict.log.warn(`Unknown SignatureSectionFontStyle "${tmpConfiguredStyle}"; falling back to Default. Valid options: ${Object.keys(SIGNATURE_FONT_STYLES).join(', ')}.`);
		return SIGNATURE_FONT_STYLES.Default;
	}

	/**
	 * @param {import('pict-view').Renderable} pRenderable
	 */
	onBeforeRender(pRenderable)
	{
		try {
			// Built fresh on every render: the section is redrawn in place when an approval action changes the status.
			this.SignatureData.SignaturesList = [];
			this.SignatureData.Font = this.resolveSignatureFontStyle();
			if (!this.AppData?.DocumentData?.ReportData?.IDDocument)
			{
				this.pict.log.error('Signature section cannot be rendered without a valid Document ID');
				return;
			}
			// The rows follow the Draft / Submit / Approve / Reject workflow: the latest submission, plus the latest
			// approval once the document is approved. A one-step workflow whose only action is Complete (LADOTD Sample
			// Creation) signs with that action in place of Submit.

			// the approval flag adds the approver's row; the final flag hides the disclaimer
			this.SignatureData.IsApproved = this.AppData?.DocumentData?.ReportData?.Status.toLowerCase() === 'approved';
			this.SignatureData.IsFinal = FINAL_STATUSES.includes(this.AppData?.DocumentData?.ReportData?.Status.toLowerCase());
			// no need to show anything if we are in draft/reject
			if (this.AppData?.DocumentData?.ReportData?.Status.toLowerCase() === 'draft' || this.AppData?.DocumentData?.ReportData?.Status.toLowerCase() === 'rejected')
			{
				return;
			}
			const reversedLogList = (this.AppData?.DocumentData?.ReportData?.ApprovalActions || []).slice().reverse();
			// if we are approved, add the approval log to the list
			if (this.SignatureData.IsApproved)
			{
				const approvalLog = reversedLogList.find((l) => l.Action.toLowerCase() === 'approve');
				if (approvalLog)
				{
					approvalLog.Label = this.formatSignatureLabel(approvalLog);
					this.SignatureData.SignaturesList.unshift(approvalLog);
				}
			}
			// for submitted/reviewed, we know we're not in Draft/Reject/Approved if we are here
			const submitLog = reversedLogList.find((l) => SUBMISSION_ACTIONS.includes(l.Action.toLowerCase()));
			if (submitLog)
			{
				submitLog.Label = this.formatSignatureLabel(submitLog);
				this.SignatureData.SignaturesList.unshift(submitLog);
			}
		}
		catch (exception)
		{
			this.pict.log.error(`Signature section cannot be rendered, ${exception}`);
		}
		return super.onBeforeRender(pRenderable);
	}

	/**
	 * @param {import('pict-view').ErrorCallback} fCallback
	 * @param {import('pict-view').Renderable} pRenderable
	 */
	onAfterRenderAsync(fCallback, pRenderable)
	{
		super.onAfterRenderAsync((pError) =>
		{
			// if approved (or otherwise final), remove the note
			if (this.SignatureData.IsFinal)
			{
				this.pict.ContentAssignment.addClass('#HeadlightSignatures-Disclaimer', 'is-hidden');
			}
			else if (this.SignatureData.SignaturesList.length)
			{
				this.pict.ContentAssignment.assignContent('#HeadlightSignatures-Disclaimer-Message', 'More signatures may appear as individuals sign this document.');
			}
			// config options to hide/show titles and dates
			if (!this.SignatureData.Options.ShowTitles)
			{
				this.pict.ContentAssignment.addClass('[data-signature-title]', 'is-hidden');
			}
			if (this.SignatureData.Options.HideDates)
			{
				this.pict.ContentAssignment.addClass('[data-signature-date]', 'is-hidden');
			}
			if (this.SignatureData.Options.HideSectionHeader)
			{
				this.pict.ContentAssignment.addClass('#HeadlightSignatures-Header', 'is-hidden');
			}
			return fCallback(pError);
		}, pRenderable);
	}

	/**
	 * Format the signature label based on the action and status
	 * @param {Object} pLogObject
	 * @returns {string}
	 * @memberof HeadlightSignatures
	 */
	formatSignatureLabel(pLogObject)
	{
		//TODO: read from config and only use this default if no config is set
		const action = pLogObject.Action
			.replace(/submit/i, 'Submitted')
			.replace(/\bcomplete\b/i, 'Completed')
			.replace(/approve/i, 'Approved')
			.replace(/reject/i, 'Rejected')
			.replace(/review/i, 'Reviewed');

		let label = `${action} By`;

		if (Object.keys(this.SignatureData.Options.CustomLabels).length > 0)
		{
			const labelObj = this.SignatureData.Options.CustomLabels.find((l) => l.status === pLogObject.Status);
			if (labelObj)
			{
				label = labelObj.label;
			}
		}
		return label;
	}

}

module.exports = HeadlightSignatures;
module.exports.default_configuration =
{
	ViewIdentifier: 'HeadlightSignatures',
	RenderOnLoad: false,
	AutoRender: false,
	DefaultRenderable: 'HeadlightSignatures',
	DefaultDestinationAddress: '#Headlight-View-Signatures',
	CSS: /*css*/`
		.hl-signature-written {
			font-optical-sizing: auto;
			font-weight: normal;
			font-style: normal;
			font-size: var(--bulma-size-4);
		}
		.hl-signature-font-default { font-family: "Rock Salt", cursive; }
		.hl-signature-font-cursive { font-family: "Corinthia", cursive; font-size: calc(var(--bulma-size-4) * 2.25); line-height: 0.75; }
		.hl-signature-font-serif { font-family: "Merriweather", serif; }
		.hl-signature-font-sans-serif { font-family: "Cabin", sans-serif; }
		.hl-signature-print {
			font-size: var(--bulma-size-7);
		}`,
	Templates:
	[
		{
			Hash: 'HeadlightSignatures',
			Template: /*html*/`
			<section style="break-inside:avoid">
				<link rel="preconnect" href="https://fonts.googleapis.com" />
				<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
				<link href="https://fonts.googleapis.com/css2?family={~D:Context[0].SignatureData.Font.URLParam~}&display=swap" rel="stylesheet" />
				<h2 id="HeadlightSignatures-Header" class="section-header">Signatures</h2>
				<div id="HeadlightSignatures-Content" class="container">
					{~TS:HeadlightSignatures-Row:Context[0].SignatureData.SignaturesList~}
				</div>
				<div class="columns m-2">
					<div class="column is-12">
						<div id="HeadlightSignatures-Disclaimer" class="notification has-text-centered empty-message">
							<span class="icon">
								<i class="fas fa-fw fa-signature"></i>
							</span>
							<span id="HeadlightSignatures-Disclaimer-Message" class="is-italic">Signatures will appear when individuals change the status of this document.</span>
						</div>
					</div>
				</div>
			</section>
`,
		},
		{
			Hash: 'HeadlightSignatures-Row',
			Template: /*html*/`
			<div class="columns m-5 mb-1" data-sign-key="Row" data-log-after-status="{~D:Record.AfterStatus~}">
				<div class="column" data-signature-name>
					<label class="label">{~D:Record.Label~}</label>
					<div class="hl-signature-written {~D:Context[0].SignatureData.Font.CSSClass~}" style="border-bottom: 1px solid #666;">{~E:User^Record.CreatingIDUser^HeadlightSignatures-Row-Name~}</div>
					<span class="hl-signature-print">{~E:User^Record.CreatingIDUser^HeadlightSignatures-Row-Name~}</span>
				</div>
				<div class="column" data-signature-title>
					<label class="label">Title / Qualification</label>
					<span>{~E:User^Record.CreatingIDUser^HeadlightSignatures-Row-Title~}</span>
				</div>
				<div class="column" data-signature-date>
					<label class="label">Date</label>
					<span>{~DateFormatTZ:Record.UpdateDate^MMMM DD, YYYY - hh:mm a^AppData.DocumentData.ReportData.Timezone~}</span>
				</div>
			</div>
`,
		},
		{
			Hash: 'HeadlightSignatures-Row-Name',
			Template: /*html*/`
			{~D:Record.NameFirst~} {~D:Record.NameLast~}
`,
		},
		{
			Hash: 'HeadlightSignatures-Row-Title',
			Template: /*html*/`
			{~D:Record.Title~}
`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'HeadlightSignatures',
			TemplateHash: 'HeadlightSignatures',
			DestinationAddress: '#Headlight-View-Signatures'
		},
	]
};
